import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightWorkbenchApi } from '../api/InsightWorkbenchApi'
import type { InsightResult } from '../models/insight.types'
import type { InsightWorkbenchResultParams } from '../models/insightApi.types'
import type { WorkbenchAsyncState } from '../models/workbench.types'
import {
  groupInsightsForHistory,
  type InsightHistoryGroup,
  type InsightHistoryGroupBy,
} from '../service/insightPresentation'
import { normalizeWorkbenchPlatformFilter } from '../service/insightWorkbench'

import { ApiError, type Paginated } from '@/shared/http'

export interface InsightHistoryFilters {
  outcome: string
  severity: string
  platform: string
  status: '' | 'pending' | 'seen' | 'clicked' | 'dismissed' | 'applied'
}

interface InsightHistoryApiPort {
  listResults: (
    accountId: number,
    workspaceId: number,
    params?: InsightWorkbenchResultParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightResult>>
  getResult: (
    accountId: number,
    workspaceId: number,
    insightId: number,
    tab?: string,
    signal?: AbortSignal,
  ) => Promise<InsightResult>
}

export interface UseInsightHistoryOptions {
  accountId: MaybeRefOrGetter<number | undefined>
  workspaceId: MaybeRefOrGetter<number | undefined>
  configEnum?: MaybeRefOrGetter<string | undefined>
  enabled?: MaybeRefOrGetter<boolean>
  api?: InsightHistoryApiPort
  limit?: number
}

export interface UseInsightHistory {
  results: ComputedRef<readonly InsightResult[]>
  groupBy: Ref<InsightHistoryGroupBy>
  grouped: ComputedRef<readonly InsightHistoryGroup[]>
  total: ComputedRef<number>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  draftFilters: Ref<InsightHistoryFilters>
  appliedFilters: ComputedRef<InsightHistoryFilters>
  selectedId: Ref<number | null>
  selected: ComputedRef<InsightResult | null>
  detailState: ComputedRef<WorkbenchAsyncState>
  detailError: ComputedRef<ApiError | null>
  detailTab: Ref<string>
  applyFilters: () => Promise<void>
  clearFilters: () => Promise<void>
  load: () => Promise<void>
  selectResult: (result: InsightResult) => Promise<void>
  reset: () => void
}

const EMPTY_FILTERS = (): InsightHistoryFilters => ({
  outcome: '',
  severity: '',
  platform: '',
  status: '',
})

export function useInsightHistory(options: UseInsightHistoryOptions): UseInsightHistory {
  const api = options.api ?? insightWorkbenchApi
  const results = ref<InsightResult[]>([])
  const total = ref(0)
  const state = ref<WorkbenchAsyncState>('idle')
  const error = ref<ApiError | null>(null)
  const draftFilters = ref(EMPTY_FILTERS())
  const appliedFilters = ref(EMPTY_FILTERS())
  const selectedId = ref<number | null>(null)
  const selectedDetail = ref<InsightResult | null>(null)
  const detailState = ref<WorkbenchAsyncState>('idle')
  const detailError = ref<ApiError | null>(null)
  const detailTab = ref('')
  const groupBy = ref<InsightHistoryGroupBy>('outcome')
  let listGeneration = 0
  let detailGeneration = 0
  let listController: AbortController | null = null
  let detailController: AbortController | null = null

  const contextKey = computed(
    () => `${toValue(options.accountId) ?? ''}:${toValue(options.workspaceId) ?? ''}`,
  )
  const configEnum = computed(() => toValue(options.configEnum)?.trim() || '')
  const enabled = computed(() => (options.enabled === undefined ? true : toValue(options.enabled)))

  function reset(): void {
    listGeneration += 1
    detailGeneration += 1
    listController?.abort()
    detailController?.abort()
    listController = null
    detailController = null
    results.value = []
    total.value = 0
    state.value = 'idle'
    error.value = null
    selectedId.value = null
    selectedDetail.value = null
    detailState.value = 'idle'
    detailError.value = null
    detailTab.value = ''
    groupBy.value = 'outcome'
  }

  async function load(): Promise<void> {
    const accountId = toValue(options.accountId)
    const workspaceId = toValue(options.workspaceId)
    if (!enabled.value || accountId === undefined || workspaceId === undefined) {
      reset()
      return
    }
    const request = ++listGeneration
    listController?.abort()
    const activeController = new AbortController()
    listController = activeController
    const requestContext = contextKey.value
    state.value = 'loading'
    error.value = null

    try {
      const filters = appliedFilters.value
      const page = await api.listResults(
        accountId,
        workspaceId,
        {
          limit: options.limit ?? 50,
          offset: 0,
          outcome_category: filters.outcome,
          severity: filters.severity,
          platform: normalizeWorkbenchPlatformFilter(filters.platform),
          ...(filters.status ? { status: filters.status } : {}),
          config_enum: configEnum.value,
        },
        activeController.signal,
      )
      if (
        request !== listGeneration ||
        activeController.signal.aborted ||
        requestContext !== contextKey.value
      ) {
        return
      }
      results.value = page.results
      total.value = page.count
      state.value = page.results.length > 0 ? 'ready' : 'empty'
      if (selectedId.value && !page.results.some((item) => item.id === selectedId.value)) {
        selectedId.value = null
        selectedDetail.value = null
        detailState.value = 'idle'
      }
    } catch (cause) {
      if (request !== listGeneration || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      results.value = []
      total.value = 0
      error.value = normalized
      state.value = 'error'
    }
  }

  async function loadDetail(): Promise<void> {
    const accountId = toValue(options.accountId)
    const workspaceId = toValue(options.workspaceId)
    const insightId = selectedId.value
    if (!enabled.value || accountId === undefined || workspaceId === undefined || !insightId) {
      return
    }
    const request = ++detailGeneration
    detailController?.abort()
    const activeController = new AbortController()
    detailController = activeController
    const requestContext = contextKey.value
    detailState.value = 'loading'
    detailError.value = null
    try {
      const detail = await api.getResult(
        accountId,
        workspaceId,
        insightId,
        detailTab.value || undefined,
        activeController.signal,
      )
      if (
        request !== detailGeneration ||
        activeController.signal.aborted ||
        requestContext !== contextKey.value ||
        insightId !== selectedId.value
      ) {
        return
      }
      selectedDetail.value = detail
      detailState.value = 'ready'
    } catch (cause) {
      if (request !== detailGeneration || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      detailError.value = normalized
      detailState.value = 'error'
    }
  }

  function selectResult(result: InsightResult): Promise<void> {
    selectedId.value = result.id
    selectedDetail.value = result
    detailTab.value = result.tabs?.[0]?.key ?? ''
    return Promise.resolve()
  }

  async function applyFilters(): Promise<void> {
    appliedFilters.value = {
      ...draftFilters.value,
      platform: normalizeWorkbenchPlatformFilter(draftFilters.value.platform),
    }
    await load()
  }

  async function clearFilters(): Promise<void> {
    draftFilters.value = EMPTY_FILTERS()
    appliedFilters.value = EMPTY_FILTERS()
    await load()
  }

  watch(
    [contextKey, configEnum, enabled],
    () => {
      reset()
      if (enabled.value) void load()
    },
    { immediate: true },
  )
  watch([detailTab, selectedId], () => {
    if (selectedId.value) void loadDetail()
  })
  onScopeDispose(reset)

  return {
    results: computed(() => results.value),
    groupBy,
    grouped: computed(() => groupInsightsForHistory(results.value, groupBy.value)),
    total: computed(() => total.value),
    state: computed(() => state.value),
    error: computed(() => error.value),
    draftFilters,
    appliedFilters: computed(() => appliedFilters.value),
    selectedId,
    selected: computed(
      () =>
        selectedDetail.value ??
        results.value.find((result) => result.id === selectedId.value) ??
        null,
    ),
    detailState: computed(() => detailState.value),
    detailError: computed(() => detailError.value),
    detailTab,
    applyFilters,
    clearFilters,
    load,
    selectResult,
    reset,
  }
}
